import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { Check, Star, Zap, Crown, ArrowRight, Loader2, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { slideUp, fadeIn } from '../lib/animations';
import { RootState, useAppDispatch } from '../store';
import { 
  createCheckoutSession, 
  selectSubscription, 
  selectSubscriptionLoading, 
  selectSubscriptionError 
} from '../store/slices/subscriptionSlice';
import Navigation from "@/components/Navigation";
import Footer from "../components/Footer";

interface PricingTierProps {
  name: string;
  price: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  isEnterprise?: boolean;
  className?: string;
  onSelect: () => void;
  loading?: boolean;
  isCurrentPlan?: boolean;
}

const PricingTier: React.FC<PricingTierProps> = ({
  name,
  price,
  description,
  features,
  isPopular = false,
  isEnterprise = false,
  className = "",
  onSelect,
  loading = false,
  isCurrentPlan = false,
}) => {
  return (
    <div className={cn(
      "relative p-8 rounded-2xl border transition-all duration-300",
      isPopular 
        ? "border-primary shadow-lg shadow-primary/10 bg-primary/5" 
        : "border-border/50 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5",
      className
    )}>
      {isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="px-4 py-1 text-sm font-medium bg-primary text-primary-foreground rounded-full">
            Most Popular
          </span>
        </div>
      )}
      
      <div className="flex flex-col space-y-6">
        <div>
          <h3 className="text-2xl font-bold">{name}</h3>
          <div className="mt-2">
            <span className="text-4xl font-bold">{price}</span>
            {!isEnterprise && <span className="text-muted-foreground">/month</span>}
          </div>
          <p className="mt-4 text-muted-foreground">{description}</p>
        </div>

        <ul className="space-y-4">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start space-x-3">
              <Check className="h-5 w-5 text-primary mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <Button 
          className={cn(
            "w-full",
            isPopular ? "bg-primary hover:bg-primary/90" : "bg-secondary hover:bg-secondary/90",
            isCurrentPlan && "bg-green-600 hover:bg-green-700",
            isEnterprise && "bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
          )}
          size="lg"
          onClick={onSelect}
          disabled={loading || isCurrentPlan}
        >
          {isCurrentPlan ? (
            "Current Plan"
          ) : loading ? (
            "Processing..."
          ) : isEnterprise ? (
            <>
              <Phone className="h-4 w-4 mr-2" />
              Contact Sales
            </>
          ) : (
            "Get Started"
          )}
        </Button>
      </div>
    </div>
  );
};

const Pricing: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const subscription = useSelector(selectSubscription);
  const loading = useSelector(selectSubscriptionLoading);
  const error = useSelector(selectSubscriptionError);

  const handleSelectPlan = async (tier: string) => {
    if (tier === 'enterprise') {
      // Handle enterprise contact sales
      window.open('mailto:sales@nuclius.ai?subject=Enterprise Plan Inquiry', '_blank');
      return;
    }
    
    try {
      await dispatch(createCheckoutSession(tier)).unwrap();
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      // Handle error (show toast, etc.)
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-1 pt-16">
        <section className="py-24 relative overflow-hidden">
          {/* Background elements */}
          <div className="absolute inset-0 -z-10 opacity-30">
            <div className="absolute right-0 top-1/4 w-1/3 h-1/3 bg-primary/5 rounded-full filter blur-3xl" />
            <div className="absolute left-0 bottom-1/4 w-1/3 h-1/3 bg-accent/5 rounded-full filter blur-3xl" />
          </div>

          <div className="container px-4 md:px-6 relative z-10">
            <div className="flex flex-col items-center text-center mb-16">
              <div className={slideUp()}>
                <span className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium bg-primary/10 text-primary mb-4">
                  Simple Pricing
                </span>
              </div>
              <h2 className={cn(slideUp(100), "text-3xl font-bold tracking-tight md:text-4xl")}>
                Choose the Perfect Plan for Your Needs
              </h2>
              <p className={cn(slideUp(200), "mt-4 max-w-2xl text-lg text-muted-foreground")}>
                Start with our free tier and upgrade as you grow. All plans include our core AI features.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
              <PricingTier
                className={slideUp(100)}
                name="Free"
                price="$0"
                description="Perfect for getting started with AI video editing."
                features={[
                  "Max input: 2.5 minutes",
                  "Max output: 15 seconds",
                  "Basic AI processing",
                  "720p export quality",
                  "Community support",
                  "Watermark included"
                ]}
                onSelect={() => handleSelectPlan('free')}
                loading={loading}
                isCurrentPlan={subscription?.tier === 'free'}
              />
              
              <PricingTier
                className={slideUp(200)}
                name="Prime"
                price="$450"
                description="Ideal for content creators and small businesses."
                features={[
                  "6,000 minutes/month input",
                  "Unlimited output length",
                  "Advanced AI processing",
                  "1080p export quality",
                  "Priority support",
                  "No watermark",
                  "Basic analytics"
                ]}
                isPopular
                onSelect={() => handleSelectPlan('prime')}
                loading={loading}
                isCurrentPlan={subscription?.tier === 'prime'}
              />
              
              <PricingTier
                className={slideUp(300)}
                name="Agency"
                price="$1,200"
                description="Perfect for agencies and growing teams."
                features={[
                  "12,000 minutes/month input",
                  "Unlimited output length",
                  "Premium AI processing",
                  "4K export quality",
                  "Priority support",
                  "Team collaboration",
                  "Advanced analytics",
                  "Custom branding",
                  "API access"
                ]}
                onSelect={() => handleSelectPlan('agency')}
                loading={loading}
                isCurrentPlan={subscription?.tier === 'agency'}
              />
              
              <PricingTier
                className={slideUp(400)}
                name="Enterprise"
                price="Starting from $10,000"
                description="For large organizations with custom requirements."
                features={[
                  "Unlimited input minutes",
                  "Unlimited output length",
                  "Custom AI training",
                  "8K export quality",
                  "Dedicated support",
                  "SLA guarantee",
                  "Custom integrations",
                  "Advanced security",
                  "White-label solution",
                  "On-premise deployment"
                ]}
                isEnterprise
                onSelect={() => handleSelectPlan('enterprise')}
                loading={loading}
                isCurrentPlan={subscription?.tier === 'enterprise'}
              />
            </div>

            {/* FAQ Section */}
            <div className="mt-32">
              <div className="flex flex-col items-center text-center mb-16">
                <div className={slideUp()}>
                  <span className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium bg-primary/10 text-primary mb-4">
                    FAQ
                  </span>
                </div>
                <h2 className={cn(slideUp(100), "text-3xl font-bold tracking-tight md:text-4xl")}>
                  Frequently Asked Questions
                </h2>
                <p className={cn(slideUp(200), "mt-4 max-w-2xl text-lg text-muted-foreground")}>
                  Have questions? We're here to help.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
                <div className={cn(slideUp(100), "space-y-4")}>
                  <h3 className="text-xl font-semibold">Can I change plans later?</h3>
                  <p className="text-muted-foreground">
                    Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.
                  </p>
                </div>
                
                <div className={cn(slideUp(200), "space-y-4")}>
                  <h3 className="text-xl font-semibold">What happens if I exceed my limits?</h3>
                  <p className="text-muted-foreground">
                    We'll notify you when you're approaching your limit. You can upgrade your plan or purchase additional minutes.
                  </p>
                </div>
                
                <div className={cn(slideUp(300), "space-y-4")}>
                  <h3 className="text-xl font-semibold">Is there a contract?</h3>
                  <p className="text-muted-foreground">
                    No long-term contracts required for Free, Prime, and Agency plans. Enterprise plans may include custom terms.
                  </p>
                </div>
                
                <div className={cn(slideUp(400), "space-y-4")}>
                  <h3 className="text-xl font-semibold">Do you offer refunds?</h3>
                  <p className="text-muted-foreground">
                    Yes, we offer a 30-day money-back guarantee for all paid plans.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Pricing; 